import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { websiteApi } from '../api/website.api';
import { sectionApi, ContentField } from '../api/section.api';
import ContentEditor from './ContentEditor';
import Modal from './Modal';
import { useNotify } from '../context/NotifyContext';

interface GalleryCategoryManagerProps {
  websiteId: string;
  section: any;
}

interface ImageModalState {
  mode: 'add' | 'edit';
  categoryId: string;
  image?: any;
}

const GalleryCategoryManager: React.FC<GalleryCategoryManagerProps> = ({ websiteId, section }) => {
  const queryClient = useQueryClient();
  const { notify, confirm } = useNotify();
  const [activeTab, setActiveTab] = useState<'categories' | 'images'>('categories');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [categoryModal, setCategoryModal] = useState<{ mode: 'add' | 'edit'; category?: any } | null>(null);
  const [imageModal, setImageModal] = useState<ImageModalState | null>(null);

  // Fetch categories
  const { data: categories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ['galleryCategories', websiteId, section._id],
    queryFn: () => websiteApi.getCategoriesForSection(websiteId, section._id),
  });

  // Fetch images
  const { data: images = [], isLoading: imagesLoading } = useQuery({
    queryKey: ['galleryImages', websiteId, section._id, selectedCategoryId],
    queryFn: () => websiteApi.getGalleryImagesForCategory(websiteId, section._id, selectedCategoryId),
    enabled: activeTab === 'images',
  });

  // Fetch category schema
  const { data: categorySchema } = useQuery({
    queryKey: ['gallerycategorySchema', section.layoutId?.layoutId],
    queryFn: () => sectionApi.getContentSchema(section.layoutId?.layoutId),
    enabled: !!section.layoutId?.layoutId,
  });

  // Define image fields directly (not using layout schema as it may have array structure)
  const imageFields: ContentField[] = [
    { name: 'url', label: 'Image', type: 'image', required: true, recommendedSize: { width: 1200, height: 900, note: '4:3' } },
    { name: 'caption', label: 'Caption', type: 'text', placeholder: 'Optional caption' },
  ];

  const invalidateCategoriesAndImages = () => {
    queryClient.invalidateQueries({ queryKey: ['galleryCategories', websiteId, section._id] });
    queryClient.invalidateQueries({ queryKey: ['galleryImages', websiteId, section._id] });
  };

  // Add category mutation
  const addCategoryMutation = useMutation({
    mutationFn: (categoryData: any) => websiteApi.createContentItem(websiteId, section._id, categoryData),
    onSuccess: () => {
      invalidateCategoriesAndImages();
      setCategoryModal(null);
    },
    onError: (error: any) => {
      notify.error(error.response?.data?.message || 'Failed to add category');
    },
  });

  // Update category mutation
  const updateCategoryMutation = useMutation({
    mutationFn: ({ categoryId, categoryData }: { categoryId: string; categoryData: any }) =>
      websiteApi.updateContentItem(websiteId, section._id, categoryId, categoryData),
    onSuccess: () => {
      invalidateCategoriesAndImages();
      setCategoryModal(null);
    },
    onError: (error: any) => {
      notify.error(error.response?.data?.message || 'Failed to update category');
    },
  });

  // Add image mutation
  const addImageMutation = useMutation({
    mutationFn: ({ categoryId, imageData }: { categoryId: string; imageData: any }) =>
      websiteApi.createGalleryImageWithCategory(websiteId, section._id, categoryId, imageData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['galleryImages', websiteId, section._id] });
      setImageModal(null);
    },
    onError: (error: any) => {
      notify.error(error.response?.data?.message || 'Failed to add image');
    },
  });

  // Update image mutation
  const updateImageMutation = useMutation({
    mutationFn: ({
      imageId,
      categoryId,
      imageData,
    }: {
      imageId: string;
      categoryId: string;
      imageData: any;
    }) => websiteApi.updateGalleryImage(websiteId, section._id, imageId, imageData, categoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['galleryImages', websiteId, section._id] });
      setImageModal(null);
    },
    onError: (error: any) => {
      notify.error(error.response?.data?.message || 'Failed to update image');
    },
  });

  // Delete category mutation (cascades to its images server-side)
  const deleteCategoryMutation = useMutation({
    mutationFn: (categoryId: string) => websiteApi.deleteContentItem(websiteId, section._id, categoryId),
    onSuccess: () => {
      invalidateCategoriesAndImages();
    },
    onError: (error: any) => {
      notify.error(error.response?.data?.message || 'Failed to delete category');
    },
  });

  // Delete image mutation
  const deleteImageMutation = useMutation({
    mutationFn: (imageId: string) => websiteApi.deleteGalleryImage(websiteId, section._id, imageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['galleryImages', websiteId, section._id] });
    },
    onError: (error: any) => {
      notify.error(error.response?.data?.message || 'Failed to delete image');
    },
  });

  const openAddImageModal = () => {
    const defaultCategoryId = selectedCategoryId || categories[0]?._id || '';
    setImageModal({ mode: 'add', categoryId: defaultCategoryId });
  };

  const openEditImageModal = (image: any) => {
    const currentCategoryId = image.categoryId?._id || image.categoryId || '';
    setImageModal({ mode: 'edit', categoryId: currentCategoryId, image });
  };

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex space-x-1 bg-nova-bg3 rounded-lg p-1">
        <button
          onClick={() => setActiveTab('categories')}
          className={`flex-1 px-4 py-2 rounded-md font-medium transition-colors ${
            activeTab === 'categories'
              ? 'bg-nova-bg3 text-nova-sky border border-nova-border'
              : 'text-nova-text2 hover:text-nova-text'
          }`}
        >
          Categories ({categories.length})
        </button>
        <button
          onClick={() => setActiveTab('images')}
          className={`flex-1 px-4 py-2 rounded-md font-medium transition-colors ${
            activeTab === 'images'
              ? 'bg-nova-bg3 text-nova-sky border border-nova-border'
              : 'text-nova-text2 hover:text-nova-text'
          }`}
        >
          Images
        </button>
      </div>

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h4 className="font-semibold text-nova-text">Categories ({categories.length})</h4>
            <button
              onClick={() => setCategoryModal({ mode: 'add' })}
              className="px-4 py-2 btn-primary rounded-lg hover:bg-nova-sky2 transition-colors"
            >
              + Add Category
            </button>
          </div>

          {categoriesLoading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-nova-sky"></div>
            </div>
          ) : categories.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {categories.map((category: any) => (
                <div
                  key={category._id}
                  className="p-4 border border-nova-border rounded-lg bg-nova-bg3 hover:border-nova-border2 transition-shadow"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      {category.image && (
                        <img
                          src={category.image}
                          alt={category.categoryName}
                          className="w-full h-32 object-cover rounded-lg mb-3"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      )}
                      <h5 className="font-semibold text-nova-text">{category.categoryName}</h5>
                      {category.description && (
                        <p className="text-sm text-nova-text2 mt-1">{category.description}</p>
                      )}
                    </div>
                    <div className="flex flex-col gap-1 ml-2">
                      <button
                        onClick={() => setCategoryModal({ mode: 'edit', category })}
                        className="p-2.5 text-nova-sky hover:bg-nova-sky/10 rounded-lg transition-colors flex items-center justify-center min-h-[44px] min-w-[44px]"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={async () => {
                          const ok = await confirm({
                            title: 'Delete category',
                            message: 'Delete this category? Any images in this category will also be deleted.',
                            confirmLabel: 'Delete',
                            danger: true,
                          });
                          if (ok) deleteCategoryMutation.mutate(category._id);
                        }}
                        className="p-2.5 text-nova-red hover:bg-nova-red/10 rounded-lg transition-colors flex items-center justify-center min-h-[44px] min-w-[44px]"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-nova-bg2 rounded-lg">
              <p className="text-nova-text3">No categories yet. Add your first category above.</p>
            </div>
          )}
        </div>
      )}

      {/* Images Tab */}
      {activeTab === 'images' && (
        <div className="space-y-6">
          {categories.length === 0 ? (
            <div className="text-center py-12 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <p className="text-yellow-400 font-medium">Please add categories first!</p>
              <button
                onClick={() => setActiveTab('categories')}
                className="mt-4 px-4 py-2 btn-primary rounded-lg hover:bg-nova-sky2"
              >
                Go to Categories
              </button>
            </div>
          ) : (
            <>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4">
                {/* Category Filter */}
                <div>
                  <label className="block text-sm font-medium text-nova-text2 mb-2">Filter by Category</label>
                  <select
                    value={selectedCategoryId}
                    onChange={(e) => setSelectedCategoryId(e.target.value)}
                    className="w-full md:w-64 px-4 py-2 border border-nova-border rounded-lg focus:ring-2 focus:ring-nova-sky/30"
                  >
                    <option value="">All Categories</option>
                    {categories.map((cat: any) => (
                      <option key={cat._id} value={cat._id}>
                        {cat.categoryName}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={openAddImageModal}
                  className="px-4 py-2 btn-primary rounded-lg hover:bg-nova-sky2 transition-colors self-start sm:self-auto"
                >
                  + Add Image
                </button>
              </div>

              {/* Existing Images */}
              {imagesLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-nova-sky"></div>
                </div>
              ) : images.length > 0 ? (
                <div>
                  <h4 className="font-semibold text-nova-text mb-3">
                    Images ({images.length})
                    {selectedCategoryId && ` in ${categories.find((c: any) => c._id === selectedCategoryId)?.categoryName}`}
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {images.map((image: any) => (
                      <div
                        key={image._id}
                        className="border border-nova-border rounded-lg bg-nova-bg3 hover:border-nova-border2 transition-shadow overflow-hidden"
                      >
                        {image.url && (
                          <img
                            src={image.url}
                            alt={image.caption || ''}
                            className="w-full h-32 object-cover"
                            onError={(e) => {
                              e.currentTarget.src = 'https://via.placeholder.com/300x200?text=No+Image';
                            }}
                          />
                        )}
                        <div className="p-3">
                          <div className="flex justify-between items-start gap-2">
                            <span className="text-xs px-2 py-1 bg-blue-100 text-nova-sky rounded truncate">
                              {image.category || image.categoryId?.categoryName}
                            </span>
                            <div className="flex gap-1 flex-shrink-0">
                              <button
                                onClick={() => openEditImageModal(image)}
                                className="p-2 -m-1 text-nova-sky hover:bg-nova-sky/10 rounded transition-colors flex items-center justify-center min-h-[36px] min-w-[36px]"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                  />
                                </svg>
                              </button>
                              <button
                                onClick={async () => {
                                  const ok = await confirm({
                                    title: 'Delete image',
                                    message: 'Delete this image?',
                                    confirmLabel: 'Delete',
                                    danger: true,
                                  });
                                  if (ok) deleteImageMutation.mutate(image._id);
                                }}
                                className="p-2 -m-1 text-nova-red hover:bg-nova-red/10 rounded transition-colors flex items-center justify-center min-h-[36px] min-w-[36px]"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                          {image.caption && (
                            <p className="text-sm text-nova-text2 mt-1 line-clamp-2">{image.caption}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 bg-nova-bg2 rounded-lg">
                  <p className="text-nova-text3">
                    {selectedCategoryId
                      ? 'No images in this category yet.'
                      : 'No images yet. Add your first image above.'}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Add/Edit Category Modal */}
      <Modal
        isOpen={!!categoryModal}
        onClose={() => setCategoryModal(null)}
        title={categoryModal?.mode === 'edit' ? 'Edit Category' : 'Add New Category'}
      >
        {categorySchema && categoryModal && (
          <ContentEditor
            key={categoryModal.category?._id || 'new-category'}
            fields={categorySchema.fields}
            initialContent={categoryModal.category || {}}
            onSave={(categoryData) =>
              categoryModal.mode === 'edit'
                ? updateCategoryMutation.mutate({ categoryId: categoryModal.category._id, categoryData })
                : addCategoryMutation.mutate(categoryData)
            }
            isSaving={addCategoryMutation.isPending || updateCategoryMutation.isPending}
          />
        )}
      </Modal>

      {/* Add/Edit Image Modal */}
      <Modal
        isOpen={!!imageModal}
        onClose={() => setImageModal(null)}
        title={imageModal?.mode === 'edit' ? 'Edit Image' : 'Add New Image'}
      >
        {imageModal && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-nova-text2 mb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                value={imageModal.categoryId}
                onChange={(e) => setImageModal({ ...imageModal, categoryId: e.target.value })}
                className="w-full px-4 py-2 border border-nova-border rounded-lg focus:ring-2 focus:ring-nova-sky/30"
              >
                <option value="">-- Select Category --</option>
                {categories.map((cat: any) => (
                  <option key={cat._id} value={cat._id}>
                    {cat.categoryName}
                  </option>
                ))}
              </select>
            </div>

            {imageModal.categoryId && (
              <ContentEditor
                key={imageModal.image?._id || 'new-image'}
                fields={imageFields}
                initialContent={imageModal.image || {}}
                onSave={(imageData) =>
                  imageModal.mode === 'edit'
                    ? updateImageMutation.mutate({
                        imageId: imageModal.image._id,
                        categoryId: imageModal.categoryId,
                        imageData,
                      })
                    : addImageMutation.mutate({ categoryId: imageModal.categoryId, imageData })
                }
                isSaving={addImageMutation.isPending || updateImageMutation.isPending}
              />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default GalleryCategoryManager;
